<template>
    <div class="pagination">
        <div v-if="totalPages > 1" class="flex justify-center gap-x-8 lg:gap-x-6">
            <button
                class="mx-1 bg-green-200 px-2.5 py-2.5 disabled:opacity-50 lg:bg-transparent lg:px-4 lg:py-1"
                :disabled="currentPage === 1"
                @click="onPreviousPage"
                aria-label="Vorige pagina">
                <span class="size-3 rotate-180 lg:size-5" aria-hidden="true"><ArrowLongLeftIcon class="size-4" /></span>
            </button>
            <button
                v-for="page in displayedPages"
                :key="page"
                @click="onGoToPage(page)"
                class="mx-1 flex h-8 w-8 flex-col items-center justify-center text-base text-black hover:bg-green-300"
                :class="{
                    'bg-green-200 text-black': currentPage === Number(page),
                    'bg-green-200': page === '...',
                }"
                :disabled="page === '...'"
                :aria-current="currentPage === Number(page) ? 'page' : undefined">
                {{ page }}
            </button>
            <button
                class="mx-1 bg-green-200 px-2.5 py-2.5 disabled:opacity-50 lg:bg-transparent lg:px-4 lg:py-1"
                :disabled="currentPage === totalPages"
                @click="onNextPage"
                aria-label="Volgende pagina">
                <span class="size-3 lg:size-5" aria-hidden="true"><ArrowLongRightIcon class="size-4" /></span>
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ArrowLongRightIcon, ArrowLongLeftIcon } from '@heroicons/vue/24/solid';
    const props = defineProps({
        currentPage: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        totalItems: {
            type: Number,
            required: true,
        },
    });

    const emit = defineEmits(['page-change', 'previous', 'next']);

    // Calculate displayed pages with ellipsis
    const displayedPages = computed(() => {
        if (props.totalPages <= 7) {
            // If 7 or fewer pages, show all pages
            return Array.from({ length: props.totalPages }, (_, i) => i + 1);
        }

        // Always show first and last page
        const pages: (number | string)[] = [1, props.totalPages];

        // Calculate pages around current page
        const leftBound = Math.max(2, props.currentPage - 1);
        const rightBound = Math.min(props.totalPages - 1, props.currentPage + 1);

        // Add ellipsis and pages
        if (leftBound > 2) pages.push('...');
        for (let i = leftBound; i <= rightBound; i++) {
            pages.push(i);
        }
        if (rightBound < props.totalPages - 1) pages.push('...');

        // Sort the pages
        return pages.sort((a, b) => {
            if (a === '...') return 0;
            if (b === '...') return 0;
            return (a as number) - (b as number);
        });
    });

    function onPreviousPage() {
        emit('previous');
        emit('page-change', props.currentPage - 1);
    }

    function onNextPage() {
        emit('next');
        emit('page-change', props.currentPage + 1);
    }

    function onGoToPage(page: number | string) {
        if (page !== '...') {
            emit('page-change', page as number);
        }
    }
</script>
